import React from 'react';

import styled from 'styled-components';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import { useFormikContext } from 'formik';

import FFWebTheme from 'ff/debrief/common/StyledComponentThemes';
import SpeedRow from './SpeedRow';
import { AircraftProfile, Vspeeds } from 'ff/debrief/models';
import { VspeedFormValues } from './VspeedFormValues';

interface Props {
    standardSpeeds: Vspeeds;
    aircraftProfile: AircraftProfile;
    isCustom: boolean;
    disabled?: boolean;
}

const VspeedsTableSection: React.FC<Props> = ({
    standardSpeeds,
    aircraftProfile,
    isCustom,
    disabled,
}) => {
    const { values, setFieldValue } = useFormikContext<VspeedFormValues>();
    const unit = values.preferredSpeedUnit || 'kts';

    const getStd = (key: keyof AircraftProfile['defaultVSpeed']) => {
        if (isCustom) return null;
        return aircraftProfile?.defaultVSpeed?.[key] ?? standardSpeeds?.[key];
    };

    const handleUnitChange = (_: unknown, newUnit: string | null) => {
        if (newUnit !== null) {
            setFieldValue('preferredSpeedUnit', newUnit);
        }
    };

    const speedRows = [
        { name: 'vr', label: 'Vr' },
        { name: 'vs0', label: 'Vs0' },
        { name: 'vs1', label: 'Vs1' },
        { name: 'vx', label: 'Vx' },
        { name: 'vy', label: 'Vy' },
        { name: 'vsw', label: 'Vsw' },
        { name: 'vref', label: 'Vref' },
        { name: 'vne', label: 'Vne' },
        { name: 'vmxt', label: 'Vmxt' },
    ] as const;

    return (
        <>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <HeaderCell
                                sx={{
                                    textAlign: 'start',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={3}
                                >
                                    Parameter
                                    <StyledToggleButtonGroup
                                        size="small"
                                        value={unit}
                                        exclusive
                                        onChange={handleUnitChange}
                                    >
                                        <StyledToggleButton value="kts">
                                            KTS
                                        </StyledToggleButton>
                                        <StyledToggleButton value="mph">
                                            MPH
                                        </StyledToggleButton>
                                    </StyledToggleButtonGroup>
                                </Stack>
                            </HeaderCell>
                            <HeaderCell
                                sx={{ textAlign: 'end', whiteSpace: 'nowrap' }}
                            >
                                Default ({unit})
                            </HeaderCell>
                            <HeaderCell
                                sx={{ textAlign: 'end', whiteSpace: 'nowrap' }}
                            >
                                Override ({unit})
                            </HeaderCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {speedRows.map(({ name, label }) => (
                            <SpeedRow
                                key={name}
                                name={name}
                                label={label}
                                standard={getStd(name)}
                                disabled={disabled}
                                unit={unit}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {isCustom && (
                <Box p={2} borderRadius={0.5} bgcolor="#EBF7FC" color="#424853">
                    Please ensure that all V-speeds overrides are entered for
                    this aircraft.
                </Box>
            )}
        </>
    );
};

const HeaderCell = styled(TableCell)`
    font-size: 11px;
    font-weight: 700;
    background-color: #f3f4f6;
    color: #6d767d;
    text-transform: uppercase;
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
    height: 24px;
`;

const StyledToggleButton = styled(ToggleButton)`
    font-size: 10px;
    font-weight: 600;
    padding: 2px 8px;
    color: ${FFWebTheme.blue};
    border-color: ${FFWebTheme.blue};

    &.Mui-selected {
        background-color: ${FFWebTheme.blue};
        color: white;

        &:hover {
            background-color: ${FFWebTheme.blueHover};
        }
    }

    &:hover {
        background-color: ${FFWebTheme.blueHighlight};
    }
`;

export default VspeedsTableSection;
